"use client";

// Third-party Imports
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

// Redux type Imports
import type { PayloadAction } from "@reduxjs/toolkit";

// Type Imports
import type { userNotificationsTypes } from "@/types/apps/userNotificationsTypes";

// Server Imports
import {
  doGetuserNotifications,
  doUpdateuserNotificationById,
  doDeleteuserNotificationById,
  doUpdateAllUserNotifications,
} from "@/app/server/actions";

const initialState: userNotificationsTypes = {
  notifications: [],
  filteredNotifications: [],
  activeNotification: {},
  loading: "idle" as string | "pending" as string | "succeeded" as
    | string
    | "failed" as string,
  error: null,
  total: 0,
  lastErrorToastTime: 0,
} as any;

export const fetch = createAsyncThunk<any, any, any>(
  "user_notifications/fetch",
  async (_: void, { rejectWithValue }) => {
    const response = await doGetuserNotifications();

    // If response is null, it means a 500 error occurred
    if (response === null || response === undefined) {
      return rejectWithValue("Failed to load notifications");
    }

    // Ensure response is an array
    const notifications = Array.isArray(response) ? response : [];

    return {
      data: notifications,
      total: notifications.length,
    };
  },
);

export const update = createAsyncThunk<any, any, any>(
  "user_notifications/update",
  async (data: any, { rejectWithValue }) => {
    try {
      await doUpdateuserNotificationById(data);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const updateAll = createAsyncThunk<any, any, any>(
  "user_notifications/update_all",
  async (data: any, { rejectWithValue }) => {
    try {
      await doUpdateAllUserNotifications(data);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const deleteNotification = createAsyncThunk<any, any, any>(
  "user_notifications/delete",
  async (data: any, { rejectWithValue }) => {
    try {
      await doDeleteuserNotificationById(data);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const userNotificationsSlice = createSlice({
  name: "userNotifications",
  initialState: initialState,
  reducers: {
    addnewNotification: (state: any, action) => {
      state.notifications.push(action.payload);
    },
    getActiveNotificationData: (state, action: PayloadAction<number>) => {
      if (!action.payload) {
        state.activeNotification = {};
      } else {
        const activeNotification = state.notifications.find(
          (notification: any) => notification._id.$oid === action.payload,
        );

        if (activeNotification) {
          activeNotification.readed = true;
          state.activeNotification = activeNotification;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetch.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(fetch.rejected, (state, action) => {
      // Don't show error if the request was aborted
      if (action.meta.aborted) {
        state.loading = "idle";
        return;
      }

      state.loading = "failed";
      state.error = action.payload;

      // Only show toast if there's an actual error and enough time has passed since last toast
      // This prevents duplicates in React Strict Mode (500ms debounce)
      const now = Date.now();
      const timeSinceLastToast = now - ((state as any).lastErrorToastTime || 0);

      if (action.payload && timeSinceLastToast > 500) {
        (state as any).lastErrorToastTime = now;
        toast.error("Failed to load notifications. Please try again.", {
          toastId: "notifications-load-error",
        });
      }
    });
    builder.addCase(fetch.fulfilled, (state, action) => {
      state.notifications = action.payload.data;
      state.filteredNotifications = action.payload.data;
      state.total = action.payload.total;
      state.loading = "succeeded";
    });
  },
});

export const { addnewNotification, getActiveNotificationData } =
  userNotificationsSlice.actions;

export default userNotificationsSlice.reducer;
